import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type TabContent = {
  scrollPosition: number;
  itemSizes?: any;
};

type IVideo = {
  channelHashId: string;
  videoHashId: string;
  selectedTab?: number;
  selectedShot?: number;
  tabContent?: Record<string, TabContent>;
  playerTime?: number;
  playerRef?: any;
  changedShot?: boolean;
  activePlayer?: boolean;
  searchKey?: string;
  // videoShots?: any;
};

export type secondaryViewState = {
  channels: Record<string, IVideo>; // Update type to store video objects directly within channel objects
};

const initialState: secondaryViewState = {
  channels: {}
};

export const getSecondaryOpenVideo = (state: secondaryViewState, channelHashId: string): IVideo | null => {
  const video = Object.values(state.channels).find((video) => video.channelHashId === channelHashId);
  return video ? video : null;
};

const SecondaryViewSlice = createSlice({
  name: 'secondary-view',
  initialState: initialState,
  reducers: {
    addVideo(state, action: PayloadAction<IVideo>) {
      const newVideo = action.payload;
      const data = {
        ...newVideo,
        selectedTab: 0,
        selectedShot: 0,
        playerTime: 0,
        changedShot: false,
        activePlayer: false
      };
      const { channelHashId } = action.payload;
      state.channels[channelHashId] = data;
    },
    removeVideo(state, action: PayloadAction<{ channelId: string }>) {
      const { channelId } = action.payload;
      delete state.channels[channelId];
    },
    setSelectedTab(state, action) {
      const { channelHashId, newSelectedTab } = action.payload;
      const video = state.channels[channelHashId];
      if (video) {
        state.channels[channelHashId] = { ...video, selectedTab: newSelectedTab };
      }
    },
    toggleActivePlayer(state, action) {
      const { channelHashId, activePlayer } = action.payload;
      const video = state.channels[channelHashId];
      if (video) {
        state.channels[channelHashId] = { ...video, activePlayer: activePlayer };
      }
    },
    setSelectedShot(state, action) {
      const { channelHashId, newSelectedShot } = action.payload;
      const video = state.channels[channelHashId];
      if (video) {
        state.channels[channelHashId] = { ...video, selectedShot: newSelectedShot };
      }
    },
    updateTabContent(
      state,
      action: PayloadAction<{
        channelHashId: string;
        data: { [tabName: string]: { scrollPosition: number; itemSizes?: any } };
      }>
    ) {
      const { channelHashId, data } = action.payload;
      const video = state.channels[channelHashId];
      if (video) {
        state.channels[channelHashId] = {
          ...video,
          tabContent: {
            ...video.tabContent,
            ...data
          }
        };
      }
    },
    setPlayerTime(state, action) {
      const { channelHashId, currentTime } = action.payload;
      const video = state.channels[channelHashId];
      if (video) {
        state.channels[channelHashId] = { ...video, playerTime: currentTime };
      }
    },
    setPlayerRef(state, action) {
      const { channelHashId, playerRef } = action.payload;
      const video = state.channels[channelHashId];
      if (video) {
        state.channels[channelHashId] = { ...video, playerRef: playerRef };
      }
    },
    seekPlayerTime(state, action) {
      const { channelHashId, time } = action.payload;
      const video = state.channels[channelHashId];
      if (video && time) {
        video.playerRef.seek(time);
      }
    },
    toggleShot(state, action) {
      const { channelHashId, toggle } = action.payload;
      const video = state.channels[channelHashId];
      if (video) {
        state.channels[channelHashId] = { ...video, changedShot: toggle };
      }
    },
    changeVideoShot(state, action) {
      const { channelHashId, videoShots, selectedShot } = action.payload;
      const video = state.channels[channelHashId];
      try {
        const source = videoShots[selectedShot];
        if (video && source) {
          video.playerRef
            .load({
              hls: source.hls_location,
              subtitleTracks: [
                {
                  id: source.id,
                  label: 'English',
                  lang: 'en',
                  kind: 'captions',
                  url: source.transcript // URL to your closed captions file (e.g., VTT file)
                }
              ]
            })
            .then(() => {
              try {
                video.playerRef.seek(0);
              } catch (err) {
                console.log('err');
              }
            });
        }
      } catch (err) {
        console.log(err);
      }
    },
    setSearchTerm(state, action: PayloadAction<{ channelHashId: string; searchTerm: string }>) {
      const { channelHashId, searchTerm } = action.payload;
      const video = state.channels[channelHashId];
      if (video) {
        state.channels[channelHashId] = { ...video, searchKey: searchTerm };
      }
    }
  }
});

export const getScrollPosition = (
  state: secondaryViewState,
  channelId: string,
  tabName: string
): number | undefined => {
  // Retrieve the video object with the specified channelId
  const video = state.channels[channelId];
  // Check if the video and tabContent exist
  if (video && video.tabContent) {
    // If the tabContent for the specified tabName exists, return its scrollPosition
    return video.tabContent[tabName]?.scrollPosition;
  } else {
    return undefined;
  }
};

export const getItemSizes = (state: secondaryViewState, channelId: string, tabName: string): number | undefined => {
  // Retrieve the video object with the specified channelId
  const video = state.channels[channelId];
  // Check if the video and tabContent exist
  if (video && video.tabContent) {
    // If the tabContent for the specified tabName exists, return its scrollPosition
    return video.tabContent[tabName]?.itemSizes;
  } else {
    return undefined;
  }
};

export const {
  addVideo,
  removeVideo,
  setSelectedTab,
  setSelectedShot,
  updateTabContent,
  setPlayerTime,
  setPlayerRef,
  seekPlayerTime,
  toggleShot,
  toggleActivePlayer,
  changeVideoShot,
  setSearchTerm
} = SecondaryViewSlice.actions;

export const secondaryViewReducers = SecondaryViewSlice.reducer;
